import { useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { DateTime } from 'luxon'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import UploadDocumentDialog, {
  EQUIPMENT_DOCUMENT_SUGGESTIONS,
} from 'components/documents/upload-document-dialog.component'
import {
  useDeleteDocumentMutation,
  useGetDocumentPresignedUrlMutation,
  useListDocumentsQuery,
} from 'store/documents/documents.api'
import { InternalDocumentType } from 'store/documents/documents.types'
import { primaryActionButtonSx } from 'pages/address/address-details/customer-details/customer-detail-fields'

type EntityDocumentsTabProps = {
  associateId: string
  internalType: InternalDocumentType
  documentTypeSuggestions?: string[]
  variant?: 'default' | 'carrier'
}

const formatExpiryDate = (value?: string | null) => {
  if (!value) return '—'
  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed.toFormat('MMM d, yyyy') : value
}

const EntityDocumentsTab = ({
  associateId,
  internalType,
  documentTypeSuggestions = [],
  variant = 'default',
}: EntityDocumentsTabProps) => {
  const [uploadOpen, setUploadOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [actionError, setActionError] = useState('')

  const { data: documents = [], isLoading } = useListDocumentsQuery(
    { associateId, internalType },
    { skip: !associateId }
  )
  const [deleteDocument] = useDeleteDocumentMutation()
  const [getPresignedUrl, { isLoading: isOpening }] =
    useGetDocumentPresignedUrlMutation()

  const handleView = async (documentId: string) => {
    setActionError('')
    try {
      const response = await getPresignedUrl(documentId).unwrap()
      if (response.presignedUrl) {
        window.open(response.presignedUrl, '_blank', 'noopener,noreferrer')
      }
    } catch {
      setActionError('Unable to open document. Please try again.')
    }
  }

  const handleDelete = async () => {
    if (!deleteTargetId) return
    setActionError('')
    try {
      await deleteDocument({
        documentId: deleteTargetId,
        associateId,
        internalType,
      }).unwrap()
      setDeleteTargetId(null)
    } catch {
      setActionError('Unable to delete document. Please try again.')
    }
  }

  const sectionSx =
    variant === 'carrier'
      ? {
          borderRadius: '6px',
          border: '1px solid',
          borderColor: 'grey.300',
          overflow: 'hidden',
        }
      : {
          borderRadius: '6px',
          border: '1px solid',
          borderColor: 'grey.300',
          bgcolor: 'grey.100',
          overflow: 'hidden',
        }

  const tableHeadSx =
    variant === 'carrier'
      ? { bgcolor: 'grey.100' }
      : undefined

  const uploadFooterSx =
    variant === 'carrier'
      ? {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
          px: 2,
          bgcolor: 'grey.100',
          borderTop: '1px solid',
          borderColor: 'grey.300',
        }
      : {
          display: 'flex',
          justifyContent: 'center',
          pt: 2,
        }

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Box sx={sectionSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx}>
              <TableCell sx={{ fontWeight: 700 }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>File Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Expiry Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Loading documents...
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No documents yet.
                </TableCell>
              </TableRow>
            ) : (
              documents.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>{document.fileName}</TableCell>
                  <TableCell>{document.documentType}</TableCell>
                  <TableCell>{formatExpiryDate(document.expiryDate)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleView(document.id)}
                        disabled={isOpening}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: '999px',
                          minWidth: 72,
                          px: 2,
                        }}
                      >
                        View
                      </Button>
                      <IconButton
                        aria-label="delete document"
                        onClick={() => setDeleteTargetId(document.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {actionError && (
          <Typography variant="body2" color="error.main" sx={{ px: 2, pb: 1 }}>
            {actionError}
          </Typography>
        )}

        <Box sx={uploadFooterSx}>
          <Button
            variant="contained"
            onClick={() => setUploadOpen(true)}
            disabled={!associateId}
            sx={primaryActionButtonSx}
          >
            + Upload
          </Button>
        </Box>
      </Box>

      <UploadDocumentDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        associateId={associateId}
        internalType={internalType}
        documentTypeSuggestions={documentTypeSuggestions}
      />

      <ConfirmationDialog
        title="Delete Document"
        open={Boolean(deleteTargetId)}
        handleClose={() => setDeleteTargetId(null)}
        successBtnText="Delete"
        successBtnTextStyle={{ color: 'error' }}
        handleSuccessBtn={handleDelete}
      >
        Are you sure you want to delete this document?
      </ConfirmationDialog>
    </Box>
  )
}

export { EQUIPMENT_DOCUMENT_SUGGESTIONS }
export default EntityDocumentsTab
